import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Map any legacy role value to one of the 3 canonical roles
const normalizeRole = (role) => {
  if (!role) return 'career_seeker';
  switch (role) {
    case 'admin':         return 'admin';
    case 'career_coach':  return 'career_coach';
    case 'career_seeker': return 'career_seeker';
    // Legacy mappings
    case 'coach_lite': case'coach_full':    return 'career_coach';
    case 'seeker_lite': case'seeker_full': case'seeker_parent': return 'career_seeker';
    default:              return 'career_seeker';
  }
};

// Determine dashboard path from role
export const getDashboardPath = (role) => {
  const normalized = normalizeRole(role);
  switch (normalized) {
    case 'admin':        return '/admin-dashboard';
    case 'career_coach': return '/career-coach-dashboard';
    default:             return '/career-seeker-dashboard';
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const loadProfile = async (userId) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      if (!error && data && isMounted?.current) {
        setUserProfile(data);
      }
    } catch (err) {
      // Profile load failure is non-fatal
    }
  };

  useEffect(() => {
    isMounted.current = true;

    // Get initial session synchronously
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (!isMounted?.current) return;
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      setLoading(false);
      if (sessionUser) {
        loadProfile(sessionUser?.id);
      }
    })?.catch(() => {
      if (isMounted?.current) setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (!isMounted?.current) return;
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      setLoading(false);
      if (sessionUser) {
        loadProfile(sessionUser?.id);
      } else {
        setUserProfile(null);
      }
    });

    return () => {
      isMounted.current = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    return { data, error };
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: { data: metadata }
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase?.auth?.signOut();
    if (!error && isMounted?.current) {
      setUser(null);
      setUserProfile(null);
    }
    return { error };
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();
    if (!error && data && isMounted?.current) setUserProfile(data);
    return { data, error };
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
      redirectTo: `${window.location?.origin}/reset-password`
    });
    return { data, error };
  };

  // Role helpers — based on normalized role
  const getRole = () => normalizeRole(userProfile?.role);
  const isAdmin = () => getRole() === 'admin';
  const isCareerCoach = () => getRole() === 'career_coach';
  const isCareerSeeker = () => getRole() === 'career_seeker';
  const isAdminOrCoach = () => ['admin', 'career_coach']?.includes(getRole());

  // Legacy aliases kept for backward compat with other pages
  const isCoach = isCareerCoach;
  const isSeeker = isCareerSeeker;
  const isRecruiter = isCareerCoach;
  const hasRole = (role) => getRole() === normalizeRole(role);

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    isAuthenticated: !!user,
    getRole,
    isAdmin,
    isCareerCoach,
    isCareerSeeker,
    isAdminOrCoach,
    isCoach,
    isSeeker,
    isRecruiter,
    isCareerCoach,
    hasRole,
    getDashboardPath: () => getDashboardPath(userProfile?.role),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
